import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutGrid, Shield, Store, Warehouse, Package, Truck, Headphones, Glasses, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { canAccess, getStoredUser, STORE_DASHBOARD_TASKS, CENTRAL_TASKS, LAB_TASKS } from '../../utils/tasks';

// Every dashboard an admin can jump to. Kept in one place so the list is
// consistent with the ProtectedRoute access rules.
const DASHBOARDS = [
  { label: 'Admin', path: '/admin/dashboard', icon: Shield },
  { label: 'Store Manager', path: '/manager/dashboard', icon: Store },
  { label: 'Lab', path: '/lab/dashboard', icon: Warehouse },
  { label: 'Central Store', path: '/central-store/dashboard', icon: Package },
  { label: 'Courier', path: '/courier/dashboard', icon: Truck },
  { label: 'Customer Support', path: '/customer-support/dashboard', icon: Headphones },
  { label: 'Sales — New Visit', path: '/visitor-entry', icon: Glasses },
];

const Wrap = styled.div`
  position: fixed;
  right: 20px;
  bottom: 78px; /* sit above the global language switcher (bottom: 22px) */
  z-index: 5000;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: 768px) {
    bottom: 70px;
    right: 16px;
  }
`;

const Fab = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: #fff;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(79, 70, 229, 0.4);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover { transform: translateY(-1px); box-shadow: 0 10px 28px rgba(79, 70, 229, 0.5); }
  &:active { transform: translateY(0); }

  svg.chev { transition: transform 0.2s ease; transform: ${p => (p.$open ? 'rotate(180deg)' : 'none')}; }
`;

const Menu = styled.div`
  position: absolute;
  right: 0;
  bottom: calc(100% + 10px);
  min-width: 250px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.2);
  padding: 0.4rem;
  animation: sw-in 0.14s ease-out;

  @keyframes sw-in {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const MenuLabel = styled.div`
  padding: 0.55rem 0.7rem 0.4rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const Item = styled.button`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  text-align: left;
  padding: 0.6rem 0.7rem;
  border: none;
  background: ${p => (p.$active ? '#eef2ff' : 'transparent')};
  color: ${p => (p.$active ? '#4338ca' : '#1e293b')};
  border-radius: 9px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: ${p => (p.$active ? 600 : 500)};
  transition: background 0.12s ease;

  &:hover { background: ${p => (p.$active ? '#e0e7ff' : '#f1f5f9')}; }
  svg { color: ${p => (p.$active ? '#4f46e5' : '#94a3b8')}; flex-shrink: 0; }
`;

const readRole = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}').role || null; } catch (_) { return null; }
};

// Where each role normally lands (same as the login screen's ROLE_HOME).
const ROLE_HOME = {
  store_manager: '/manager/dashboard',
  lab_manager: '/lab/dashboard',
  technician: '/technician/dashboard',
  sales_person: '/visitor-entry',
  inventory_manager: '/central-store/dashboard',
  operations_manager: '/central-store/dashboard',
};

// Dashboards a non-admin can reach through task switches, with the roles that
// reach them anyway. Only dashboards OUTSIDE the employee's own role are
// offered, so the button appears just for people the admin gave extra tasks.
const TASK_DASHBOARDS = [
  { path: '/manager/dashboard', tasks: STORE_DASHBOARD_TASKS, roles: ['store_manager'] },
  { path: '/central-store/dashboard', tasks: CENTRAL_TASKS, roles: ['inventory_manager', 'operations_manager', 'courier'] },
  { path: '/lab/dashboard', tasks: LAB_TASKS, roles: ['lab_manager', 'technician'] },
  { path: '/visitor-entry', tasks: ['sales.create'], roles: ['sales_person', 'store_manager'] },
];

const dashboardsFor = (user) => {
  if (!user) return [];
  const extra = TASK_DASHBOARDS.filter((d) => !d.roles.includes(user.role) && canAccess(user, d.tasks, d.roles));
  if (extra.length === 0) return [];
  const home = ROLE_HOME[user.role];
  const paths = [...new Set([home, ...extra.map((d) => d.path)].filter(Boolean))];
  return DASHBOARDS.filter((d) => paths.includes(d.path));
};

const DashboardSwitcher = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Close the menu whenever the route changes.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const role = readRole();
  // Advisors jump between dashboards like an admin (they only view, unless
  // the admin gave them Edit on a section).
  const isAdmin = role === 'admin' || role === 'super_admin' || role === 'advisor';
  const path = location.pathname;
  const onAuthPage = path === '/' || path.startsWith('/admin/login');

  // Admins get every dashboard; anyone else only when the admin gave them
  // tasks on another dashboard. Never on the login screens.
  const items = isAdmin ? DASHBOARDS : dashboardsFor(getStoredUser());
  if (onAuthPage || items.length === 0) return null;

  return (
    <Wrap ref={ref}>
      {open && (
        <Menu>
          <MenuLabel>{t('dashSwitcher.switchDashboard')}</MenuLabel>
          {items.map(({ label, path: p, icon: Icon }) => (
            <Item
              key={p}
              $active={path.startsWith(p)}
              onClick={() => { setOpen(false); navigate(p); }}
            >
              <Icon size={16} />
              {label}
            </Item>
          ))}
        </Menu>
      )}
      <Fab $open={open} onClick={() => setOpen((v) => !v)} title={t('dashSwitcher.switchDashboard')} aria-label={t('dashSwitcher.switchDashboard')}>
        <LayoutGrid size={18} />
        {t('dashSwitcher.dashboards')}
        <ChevronDown size={15} className="chev" />
      </Fab>
    </Wrap>
  );
};

export default DashboardSwitcher;
